// Comparison-value position detector (autocomplete Phase 3 — enum-value
// suggestions).
//
// Pure string/token scan, NO grammar dependency, NO `ResolverPositionKind`. Layered
// on top of the existing `where`/`join-on`/`having` resolver branch: given
// the buffer text + cursor offset, decide whether the cursor sits at a
// *comparison value operand* — i.e. the RHS string-literal/empty operand of
// `<column> <comparison-op> <cursor>` — and, if so, return the LHS column
// reference text (`status`, `o.status`, `s.public.t.status`).
//
// Recognised operators / forms:
//   = | != | <> | <= | >= | < | >
//   LIKE | ILIKE
//   IN ( | NOT IN (         (cursor at any list slot inside the parens)
//   BETWEEN … AND | NOT BETWEEN … AND   (cursor at either bound)
//
// The detector deliberately rejects:
//   - the LHS column position itself (`WHERE stat| = 1`)
//   - select-list / non-comparison positions (`SELECT sta| FROM t`)
//   - function-call / arithmetic operands (`UPPER(status) = '|'` → the
//     immediate LHS token is a `)` paren, not a column ref → undefined;
//     `qty + 1 = '|'` → LHS token is a number, not an identifier → undefined)
//
// Returns `{ columnRef }` (the raw dotted identifier text, un-folded — the
// caller resolves it against the snapshot using each resolver's existing
// fold) or `undefined`.
//
// Backward-scan primitives live in `./comparisonScanHelpers` (split out to
// keep this file ≤200 lines).

import {
  COMPARISON_OPERATORS,
  endsWithAt,
  readDottedIdentifierBefore,
  readKeywordBefore,
  scanInOrBetweenHead,
  skipValueOperand,
} from './comparisonScanHelpers';

export type ComparisonValueContext = {
  readonly columnRef: string;
};

export function detectComparisonValueContext(
  text: string,
  offset: number,
): ComparisonValueContext | undefined {
  if (offset < 0 || offset > text.length) return undefined;

  // 1. Validate the cursor's IMMEDIATE operand only. Walk left over
  //    whitespace; the first meaningful char must be either:
  //   - `'` → the cursor is inside / right after an in-progress string
  //     literal (the value operand). `skipValueOperand` walks the literal.
  //   - `,` / `(` / a comparison-operator char → the value operand is empty
  //     (`= |`, `IN (|`, `'a', |`), still a clean value position.
  //   - newline / start-of-buffer → nothing precedes; not a comparison.
  //  Anything else (identifier char, digit, `)`) means the cursor is NOT at a
  //  clean value operand (it's the LHS column, a number, a func result).
  {
    let j = offset - 1;
    while (j >= 0 && (text[j] === ' ' || text[j] === '\t')) j -= 1;
    if (j < 0 || text[j] === '\n') return undefined;
    const ch = text[j]!;
    if (
      ch !== "'" &&
      ch !== ',' &&
      ch !== '(' &&
      ch !== '=' &&
      ch !== '<' &&
      ch !== '>' &&
      ch !== '!' &&
      // LIKE-pattern wildcards inside an in-progress pattern literal —
      // `special_features LIKE '%▮Behind the Scenes%'`. The value the user is
      // reaching for there is still one of the column's members, so the caret
      // IS a value operand (mysql coverage script.16 §3). The boundary scan
      // below re-validates that a real comparison/LIKE precedes, so a modulo
      // RHS (`a % ▮`) still falls out: `%` is not an operator tail it accepts.
      ch !== '%' &&
      ch !== '_' &&
      !/[A-Za-z]/.test(ch) // possible tail of LIKE / IN / BETWEEN keyword
    ) {
      // Not a clean value operand. Anything other than `'` (in-progress
      // literal), `,`/`(`/an operator char (empty operand), or a
      // LIKE/IN/BETWEEN keyword tail means the cursor is the LHS column, a
      // number, or a function `)` — not a value position.
      return undefined;
    }
    // Otherwise fall through to the boundary scan, which re-validates that an
    // actual comparison operator / IN / BETWEEN precedes (a stray identifier
    // like `WHERE stat|` is rejected there — no operator boundary precedes it).
  }

  // 2. Find the boundary just left of the value operand: skip the optional
  //    string literal, leading list items (`'a', 'b', |`), the open paren of
  //    an `IN ( … )` / `BETWEEN`, and surrounding whitespace — landing on the
  //    character that ends the comparison operator (or `IN` / `BETWEEN`).
  const i = skipValueOperand(text, offset - 1);
  if (i < 0) return undefined;

  // We are now positioned at the last non-skipped char before the operand.
  // It must be the tail of a comparison operator, OR we are inside an
  // `IN (...)` / `BETWEEN ... AND ...` whose head we still need to find.
  const opEnd = i + 1;

  // 2a. `IN ( … )` / `BETWEEN … AND …` (optionally `NOT`-negated): walk back
  //     across list items / the `AND` keyword + bound expression to reach the
  //     `IN`/`BETWEEN` keyword and the column before it.
  const inOrBetween = scanInOrBetweenHead(text, i);
  if (inOrBetween !== undefined) {
    const columnRef = readDottedIdentifierBefore(text, inOrBetween);
    return columnRef ? { columnRef } : undefined;
  }

  // 2b. Symbolic comparison operator (`=`, `<>`, `<=`, …).
  for (const op of COMPARISON_OPERATORS) {
    if (endsWithAt(text, opEnd, op)) {
      const before = opEnd - op.length;
      const columnRef = readDottedIdentifierBefore(text, before);
      if (!columnRef) return undefined;
      return { columnRef };
    }
  }

  // 2c. Word operators: LIKE / ILIKE.
  const word = readKeywordBefore(text, opEnd);
  if (word === 'LIKE' || word === 'ILIKE') {
    const before = opEnd - word.length;
    const columnRef = readDottedIdentifierBefore(text, before);
    return columnRef ? { columnRef } : undefined;
  }

  // 2d. Simple-CASE branch label: `CASE <selector> WHEN '▮'`. The comparison
  //     is implicit — the WHEN operand is matched against the CASE selector —
  //     so there is no operator token to key off, and the caret used to fall
  //     out of this detector entirely (mysql script.49 §4). The selector is
  //     the column whose values belong here, exactly as if the user had
  //     written `WHERE <selector> = '▮'`.
  if (word === 'WHEN') {
    const selector = simpleCaseSelectorBefore(text, opEnd - word.length);
    return selector ? { columnRef: selector } : undefined;
  }

  return undefined;
}

/**
 * The selector expression of the simple CASE whose `WHEN` ends at `whenStart`,
 * when that selector is a plain (possibly dotted) column reference.
 *
 * Walks back to the CASE that opens this expression — skipping any complete
 * nested `CASE … END`, which is why a depth counter is needed rather than a
 * search for the nearest `CASE` — then reads the text between it and the FIRST
 * `WHEN`. This is what makes the second and later branches work:
 * `CASE rating WHEN 'R' THEN 'high' WHEN '▮'` has a string literal, not the
 * selector, immediately before the caret's `WHEN`.
 *
 * Returns undefined for a SEARCHED case (`CASE WHEN x = 1 THEN …`), where the
 * selector slot is empty and each branch carries its own full predicate, and
 * for any selector that is not a bare column reference (a function call or an
 * arithmetic expression has no column whose value set we could offer).
 */
function simpleCaseSelectorBefore(text: string, whenStart: number): string | undefined {
  let depth = 0;
  let i = whenStart;
  // The selector ends at the FIRST `WHEN` of this CASE, not at the caret's —
  // every branch after the first has a THEN result between it and the caret.
  // The walk-back overwrites this with each earlier depth-0 `WHEN` it meets,
  // so by the time it reaches the opener it holds the first one.
  let firstWhenStart = whenStart;
  while (i > 0) {
    const kw = previousWord(text, i);
    if (!kw) return undefined;
    if (kw.word === 'END') depth += 1;
    else if (kw.word === 'WHEN') {
      if (depth === 0) firstWhenStart = kw.start;
    } else if (kw.word === 'CASE') {
      if (depth === 0) {
        const selector = text.slice(kw.end, firstWhenStart).trim();
        // A bare, possibly dotted/quoted identifier — nothing else.
        return /^[A-Za-z_`"[][A-Za-z0-9_`"[\].$]*$/.test(selector) && selector.length > 0
          ? selector
          : undefined;
      }
      depth -= 1;
    }
    i = kw.start;
  }
  return undefined;
}

/** The word token ending before `from`, with its source range. Non-word
 *  characters (punctuation, quotes, operators) are consumed as single-char
 *  "words" so the scan always advances. */
function previousWord(
  text: string,
  from: number,
): { readonly word: string; readonly start: number; readonly end: number } | undefined {
  let j = from - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j -= 1;
  if (j < 0) return undefined;
  const end = j + 1;
  if (!/[A-Za-z0-9_]/.test(text[j]!)) return { word: text[j]!, start: j, end };
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j]!)) j -= 1;
  return { word: text.slice(j + 1, end).toUpperCase(), start: j + 1, end };
}
